import { Injectable } from '@angular/core';
import { ILane } from '@app/core/model/runs/lane';
import { LaneStatus } from '@app/core/model/runs/lane-status';
import { ISequencingStats } from '@app/core/model/runs/seq-stats';
import { genericRetryWhen, observableEmitDelay } from '@app/core/rxjsutils/rxjs-utilities';
import { BsApiService } from '@app/core/services/bs-api/bs-api-service';
import { ErrorMessages } from '@app/core/utilities/error-messages';
import { ChangeLaneQcService } from '@app/runs/services/change-lane-qc.service';
import { delay, finalize, retryWhen, shareReplay } from 'rxjs/operators';
import { ILanesState } from './lanes.state';
import { IBulkUpdateResponse, IErrorState, IListResponseWrapper } from '@app/core/model/v2-api/v2-api-wrappers';
import { IObservableStore, BaseObservableStore } from '@app/core/store/infrastructure/base-observable.store';
import { isNullOrUndefined } from 'util';
import { V2RunLaneIndexSummary } from '../../../bsshapi/api-wrappers';
import { Observable, of } from 'rxjs';
import { RunLaneIndexService } from '../../../../runs/services/run-lane-index.service';
import { BehaviorSubject } from 'rxjs';
import { AbstractResourceStore } from '../../resource/abstract.resource.store';
import { ResourceType } from '@app/core/model/resource-type';

// Interface
export interface ILanesStore extends IObservableStore<ILanesState> {
    loadSequencingStats(): void;
    updateSelectedLanes(laneNumbers: number[]): void;
    canChangeLaneQc(): boolean;
    changeLaneQc(newStatus: LaneStatus, comments: string): void;
    canDisplayIndexQcContent(): boolean;
    getLaneIndexSummaries(): Observable<IListResponseWrapper<V2RunLaneIndexSummary>>;
    unloadSequencingStats(): void;
}

@Injectable({
    providedIn: 'root'
})
export class LanesStore extends AbstractResourceStore<ILanesState> implements ILanesStore {

    private refreshSubject = new BehaviorSubject<boolean>(false);
    lanesListRefreshed$ = this.refreshSubject.asObservable();

    // pass in the state slices we are interested in...
    constructor(private bsApi: BsApiService, private changeLaneQcService: ChangeLaneQcService,
                private runLaneIndexService: RunLaneIndexService) {
        super(['sequencingStats', 'selectedLanes', 'laneStateError',
            'currentRun', 'currentUserId', 'currentLanesRunId']);
    }

    /**
     * Calls API to load the lanes associated with the current run and cache in the lanes store
     * Assuming tht the current run is already populated before we call this.
     */
    public loadSequencingStats(refreshState: boolean = false): void {

        const currentState = this.getState();
        if (isNullOrUndefined(currentState) || isNullOrUndefined(currentState.currentRun)) {
            throw new Error(ErrorMessages.CURRENT_RUN_NOT_AVAILABLE);
        }

        const runId = currentState.currentRun.V1Pre3Id;
        if (currentState && currentState.currentLanesRunId &&
            currentState.currentLanesRunId === runId && !refreshState) {
            // if run id already set don't fetch, but dispatch current state if another call is made later
            this.dispatchCurrentState(currentState);
        } else {

            this.loadingSubject.next(true);
            const stats$ = this.bsApi.getRunSequencingStats(runId);

            // Subscribe to retrieve
            this.subs.sink = stats$.pipe(
                // Do not make an additional API call if any additional subscribers have subscribed later on
                shareReplay(1),
                // Retry in case of HTTP errors
                retryWhen(genericRetryWhen()),
                // To avoid a Flash of content, maintain a delay
                delay(observableEmitDelay),
                // Stop loading in finalize.
                finalize(() => this.loadingSubject.next(false))
            ).subscribe({
                next: (stats: ISequencingStats) => {
                    this.setState({ sequencingStats: stats, currentLanesRunId: runId, laneStateError: null, selectedLanes: null },
                        LaneStoreActions.LoadSequencingStats);
                },
                // tslint:disable-next-line: max-line-length
                error: error => this.handleError(error, () => ({ ...currentState, laneStateError: error, sequencingStats: null, selectedLanes: null }))
            });
        }
    }

    /**
      * set state based on page context. Update runlist and selected runs if page context is
      * master list.
      * Updates runlist, selected runs and the current run if page context is
      * details page.
      * @param propertyChange: property that are changing
      * @param updatedLanes: the lanes acted upon
      * @param actionName: name of action based on service
      * @param error: error in case of partial success ( i.e in case of list of resources)
      * @param forceRefresh: whether to refresh runs list if action was performed from list page
      */
    private setStateOnSuccess(propertyChange: object, updatedLanes: ILane[], actionName: LaneStoreActions,
                              error: any = null, forceRefresh?: boolean) {

        const { selectedLanes, sequencingStats } = { ...this.getState() };
        let newSelectedLanesList = selectedLanes;
        let newSequencingStats = sequencingStats;

        if (isNullOrUndefined(selectedLanes) || selectedLanes.length === 0) {
            return;
        }

        updatedLanes.forEach(lane => {
            newSelectedLanesList = this.getUpdatedLanesList(lane, selectedLanes, propertyChange);
            newSequencingStats = this.getUpdatedSequencingStats(lane, sequencingStats, propertyChange);
        });

        this.setState({
            selectedLanes: newSelectedLanesList,
            sequencingStats: newSequencingStats,
            laneStateError: error,
        },
            actionName);

        this.refreshSubject.next(forceRefresh);
    }

    private getUpdatedLanesList(lane: ILane, lanesList: ILane[], propertyUpdate: object): ILane[] {
        if (!isNullOrUndefined(lanesList)) {
            const idx = lanesList.findIndex(l => l.Id === lane.Id);
            if (!isNullOrUndefined(idx) && idx !== -1) {
                lanesList[idx] = Object.assign(lanesList[idx], propertyUpdate);
            }
        }
        return lanesList;
    }

    private getUpdatedSequencingStats(lane: ILane, sequencingStats: ISequencingStats, propertyUpdate: object) {
        const lanesList = sequencingStats.Lanes;
        if (!isNullOrUndefined(lanesList)) {
            const idx = lanesList.findIndex(l => l.Id === lane.Id);
            if (!isNullOrUndefined(idx) && idx !== -1) {
                lanesList[idx] = Object.assign(lanesList[idx], propertyUpdate);
            }
        }
        sequencingStats.Lanes = lanesList;
        return sequencingStats;
    }


    public updateSelectedLanes(laneNumbers: number[]): void {
        const { sequencingStats } = { ...this.getState() };
        const allLanes: ILane[] = sequencingStats.Lanes;
        const selectedLanes: ILane[] = allLanes.filter(({ LaneNumber }) => laneNumbers.includes(LaneNumber));
        this.setState({ ...this.getState(), selectedLanes: selectedLanes }, LaneStoreActions.UpdateSelectedLanes);
    }

    public getActiveList(): ILane[] {
        const { selectedLanes } = { ...this.getState() };

        if (isNullOrUndefined(selectedLanes)) {
            return null;
        }

        return selectedLanes;
    }

    public canChangeLaneQc(): boolean {
        const { selectedLanes, currentRun, currentUserId } = { ...this.getState() };
        return this.changeLaneQcService.canChangeLaneQc(selectedLanes, currentRun, currentUserId);
    }

    public changeLaneQc(newStatus: LaneStatus, comments: string): void {
        const { selectedLanes } = { ...this.getState() };
        this.loadingSubject.next(true);

        this.changeLaneQcService.changeLaneQc(selectedLanes, newStatus, comments).pipe(
            finalize(() => this.loadingSubject.next(false))
        ).subscribe({
            next: (apiResponses: IBulkUpdateResponse<ILane>) => {
                const totalSuccessCount = apiResponses.TotalSuccessCount;
                const totalFailureCount = apiResponses.TotalFailureCount;

                const updatedLanes = [];
                if (totalSuccessCount > 0) {
                    apiResponses.SuccessItems.forEach(item => {
                        const lane = selectedLanes.find(lane => lane.Id === item.Id);
                        updatedLanes.push(lane);
                    });
                }

                let laneUpdateError: IErrorState = null;
                if (totalFailureCount > 0) {
                    laneUpdateError = {
                        errorMessage: ErrorMessages.FAILED_TO_UPDATE_LANE_QC,
                        data: apiResponses.FailureItems
                    };
                }

                this.setStateOnSuccess({ Status: newStatus}, updatedLanes,
                    LaneStoreActions.ChangeLaneQc, laneUpdateError, true);
            },
            error: error => {
                this.handleError(error, () => ({ ...this.getState(), laneStateError: error }));
            }
        });
    }

    canDisplayIndexQcContent(): boolean {
        const { currentRun } = { ...this.getState() };
        if (!isNullOrUndefined(currentRun) && !currentRun.IsArchived) {
            return true;
        }
        return false;
    }

    getLaneIndexSummaries(): Observable<IListResponseWrapper<V2RunLaneIndexSummary>> {
        const { currentRun } = { ...this.getState() };
        return this.runLaneIndexService.getLaneIndexSummaries(currentRun.V1Pre3Id);
    }
    
    unloadSequencingStats(): void {
        this.setState({ sequencingStats: null, selectedLanes: null, currentLanesRunId: null }, LaneStoreActions.UnloadSequencingStats);
    }

    getResourceType(): ResourceType {
        return ResourceType.LANES;
    }

}

// The action-names for this store, for logging/tracking.
export enum LaneStoreActions {
    LoadSequencingStats = 'LOAD_SEQUENCING_STATS',
    UpdateSelectedLanes = 'UPDATE_SELECTED_LANES',
    ChangeLaneQc = 'CHANGE_LANE_QC',
    UnloadSequencingStats = 'UNLOAD_SEQUENCING_STATS',
}
